import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';
import { Result } from '../../types';

// Register gothic font for certificate title
Font.register({
  family: 'UnifrakturCook',
  src: 'https://fonts.gstatic.com/s/unifrakturcook/v25/IurA6Yli8YOdcoky-0PTTdkm56n05Uw1.ttf',
  fontWeight: 'bold',
});

const CERT_BG = '/certificate-bg.jpg';

const styles = StyleSheet.create({
  page: {
    position: 'relative',
    backgroundColor: '#fff',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  content: {
    position: 'absolute',
    top: 30,
    left: 80,
    right: 80,
    bottom: 155,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontFamily: 'UnifrakturCook',
    fontSize: 54,
    color: '#00838f',
    textAlign: 'center',
    marginBottom: 18,
  },
  awardedTo: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  studentName: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#222',
    textAlign: 'center',
    marginBottom: 18,
    letterSpacing: 4,
  },
  subjectGrade: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 4,
  },
  schoolName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 10,
  },
  description: {
    fontSize: 13,
    color: '#555',
    textAlign: 'center',
    marginBottom: 2,
  },
  academicYear: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
  },
});

function getCertificateTitle(awarded: string): string {
  const a = (awarded || '').trim().toUpperCase();
  if (a.includes('NATIONAL')) return 'Certificate of National Merit';
  if (a.includes('ZONAL')) return 'Certificate of Zonal Merit';
  if (a.includes('SCHOOL')) return 'Certificate of School Merit';
  if (a.includes('PARTICIPATION')) return 'Certificate of Participation';
  return 'Certificate of Merit';
}

function getCertificateDescription(awarded: string): string {
  const a = (awarded || '').trim().toUpperCase();
  if (a.includes('NATIONAL')) return 'for top performance at National level in the';
  if (a.includes('ZONAL')) return 'for top performance at Zonal level in the';
  if (a.includes('SCHOOL')) return 'for top performance at School level in the';
  if (a.includes('PARTICIPATION')) return 'for a spirited participation in the';
  return 'for performance in the';
}

const CertificatePage = React.memo(({ result, schoolName }: { result: Result; schoolName: string }) => {
  const fullName = [result.firstName, result.lastName].filter(Boolean).join(' ').toUpperCase();
  const subjectGrade = `${result.subject}, ${result.grade}`;
  const title = getCertificateTitle(result.awarded);
  const description = getCertificateDescription(result.awarded);

  return (
    <Page size="A4" orientation="landscape" style={styles.page}>
      <View style={{ position: 'relative', width: '100%', height: '100%' }}>
        <Image src={CERT_BG} style={styles.background} />
        <View style={styles.content}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.awardedTo}>awarded to</Text>
          <Text style={styles.studentName}>{fullName}</Text>
          <Text style={styles.subjectGrade}>{subjectGrade}</Text>
          <Text style={styles.schoolName}>{schoolName}</Text>
          <Text style={styles.description}>{description}</Text>
          <Text style={styles.academicYear}>Global LiveOlympiad 2025-26</Text>
        </View>
      </View>
    </Page>
  );
});

interface CertificatePdfProps {
  results: Result[];
  schoolName: string;
}

const CertificatePdf: React.FC<CertificatePdfProps> = ({ results, schoolName }) => {
  return (
    <Document>
      {results.map((result) => (
        <CertificatePage key={result.id} result={result} schoolName={schoolName} />
      ))}
    </Document>
  );
};

export default CertificatePdf;
